import { useState, useEffect } from 'react';
import { Tablet } from 'lucide-react';
import { KMCourtCard, KMScoreModal } from '../km';
import { deviceFillBoxStyle } from './deviceFill';

export interface KMSingleCourtPadProps {
  /** The Klubbmesterskap (club championship) tournament. */
  tournament: any;
  /** The single court whose pad is rendered. */
  court: any;
  /** All players (resolves names/ELO in KMCourtCard). */
  allPlayers?: any[];
  /** Records a result (the updated match object). */
  onRecordResult?: any;
  /** Persists the in-progress (live) score without finalising: (updatedMatch). */
  onLiveScore?: any;
  /** Called when a player name is clicked. */
  onPlayerClick?: any;
  /**
   * Live clock from the parent (the pad shell's, which is also what resolved
   * which session belongs on this court). Falls back to an internal 1s tick
   * when omitted, so standalone renders and the tournament-view call sites are
   * unchanged.
   */
  now?: any;
  /** Device kiosk mode: fill the whole tablet (forwarded to KMCourtCard + empty state). */
  fill?: boolean;
}

// Én fysisk pad for Klubbmesterskap — én banes KMCourtCard med game/sett-modal.
// Samme prinsipp som CourtPadTab(onlyCourt) for Padel24. Brukes i banepad-sentralen.
/**
 * Single-court banepad for the club championship (Klubbmesterskap) — one court's
 * KMCourtCard with a game/set score modal, ticking a live clock once per second.
 * Extracted verbatim from the monolith (legacy/App.tsx); markup/logic unchanged.
 */
export function KMSingleCourtPad({
  tournament,
  court,
  allPlayers = [],
  onRecordResult,
  onLiveScore,
  onPlayerClick,
  now: nowProp,
  fill = false,
}: KMSingleCourtPadProps) {
  // The pad shell's clock when it threads one down; see CourtPadTab.
  const [ownNow, setOwnNow] = useState(() => new Date());
  useEffect(() => {
    if (nowProp) return undefined;
    const id = setInterval(() => setOwnNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [nowProp]);
  const now = nowProp ?? ownNow;
  const [scoreModalMatch, setScoreModalMatch] = useState(null);
  // Local date format (NEVER toISOString) per project rules — see #323 and the
  // note in CourtPadTab.
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const clock = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const kmStarted = tournament.status !== 'ready';
  // The KM accent is the AA shade of its pink (#1295, as #1293 did for the KM
  // category list): white on #ec4899 was 3.53:1, on #c01074 it is 5.89:1.
  const accent = '#c01074';
  const courtMatches = (tournament.matches || []).filter((m) => Number(m.court) === Number(court));
  const allDivisions = (tournament.config?.classes || []).flatMap((cls) =>
    (cls.divisions || []).map((div) => ({ ...div, className: cls.name, classGender: cls.gender })),
  );
  const save = (updatedMatch) => {
    if (!kmStarted) return;
    onRecordResult?.(updatedMatch);
    setScoreModalMatch(null);
  };
  const openScore = (m) => {
    if (!kmStarted) return;
    setScoreModalMatch(m);
  };
  if (courtMatches.length === 0) {
    return (
      <div
        className="rounded-2xl flex items-center justify-center"
        style={{
          aspectRatio: '4 / 3',
          background: 'linear-gradient(165deg, #0a0b10 0%, #14161f 100%)',
          color: 'rgba(230,251,255,0.5)',
          ...deviceFillBoxStyle(fill),
        }}
      >
        <div className="text-center">
          <Tablet size={28} className="mx-auto mb-2 opacity-40" />
          <div className="font-mono text-[11px] uppercase tracking-wider">
            Ingen kamper på bane {court}
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <KMCourtCard
        court={court}
        fill={fill}
        matches={courtMatches}
        pairs={tournament.pairs}
        allPlayers={allPlayers}
        divisions={allDivisions}
        nowMin={nowMin}
        today={today}
        clock={clock}
        locked={!kmStarted}
        onSaveScore={save}
        onScoreChange={kmStarted ? onLiveScore : null}
        onClickScore={openScore}
        onPlayerClick={onPlayerClick}
      />
      {scoreModalMatch && (
        <KMScoreModal
          match={scoreModalMatch}
          pairs={tournament.pairs}
          onClose={() => setScoreModalMatch(null)}
          onSave={save}
          accentColor={accent}
        />
      )}
    </>
  );
}
